import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "How fast does SAINT® TMS work?",
    answer:
      "The SAINT® protocol itself is delivered over five consecutive days. In the Stanford clinical trials, some participants showed measurable improvement by the end of the treatment week. However, individual response varies, and the protocol's five-day schedule describes the treatment timeline, not a guaranteed result. Some people notice improvement quickly, others more gradually, and some do not respond.",
  },
  {
    question: "Does SAINT® work in five days?",
    answer:
      "The treatment is delivered over five days. The 2022 double-blind, sham-controlled trial reported that 78.6% of the active treatment group met remission criteria at four weeks, compared with 13.3% of the sham group. These are trial results, not guaranteed patient outcomes. Improvement may be noticed during treatment, in the days afterward, or at the four-week follow-up.",
  },
  {
    question:
      "What is the difference between treatment duration and when you feel better?",
    answer:
      "Treatment duration is how long the protocol takes to deliver — five consecutive days for SAINT®. When you feel better is an individual response that may happen during the treatment week, in the following weeks, or not at all. The trial measured outcomes at four weeks after treatment, not immediately on day five.",
  },
  {
    question:
      "What did the Stanford SAINT® trials show about speed of improvement?",
    answer:
      "In the 2020 open-label study, the average time to remission among responders was 2.6 days. In the 2022 randomized controlled trial, remission was measured at four weeks, with 78.6% of the active group in remission versus 13.3% of the sham group. These results come from small, single-site studies and do not guarantee individual outcomes.",
  },
  {
    question: "Can I expect immediate results from SAINT® TMS?",
    answer:
      "No treatment can guarantee immediate results. Some patients in the SAINT® trials showed improvement during the five-day treatment week, and the average time to remission among responders was 2.6 days in the open-label study. However, individual response varies widely, and some patients do not respond. A consultation with a psychiatrist can help set realistic expectations based on your history.",
  },
  {
    question: "How long do the effects of SAINT® last?",
    answer:
      "This is not yet well established. The randomized trial followed participants for four weeks. Open-label follow-up suggested that some people who improved saw symptoms return within months, and some were retreated. A maintenance plan — which may include medication, psychotherapy, or the possibility of another course — should be discussed with your psychiatrist before treatment begins.",
  },
];

export const pageHowFastSaint: LearnPageData = {
  slug: "how-fast-does-saint-tms-work",
  title: "How Fast Does SAINT® TMS Work? | Neuro Wellness",
  description:
    "How quickly SAINT® TMS works: the five-day protocol, trial data on time to improvement, the difference between treatment duration and individual response, and what to expect.",
  canonical: "/learn/how-fast-does-saint-tms-work",
  h1: "How Quickly Can SAINT® TMS Work?",
  breadcrumbLabel: "How Fast SAINT® TMS Works",
  crumbs: baseCrumbs("How Fast SAINT® TMS Works"),
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is delivered over five consecutive days — ten short sessions per day. The protocol's five-day schedule describes the treatment timeline, not a guaranteed result. In the Stanford clinical trials, some participants showed improvement during the treatment week, and the average time to remission among responders was 2.6 days in the open-label study. Individual response varies, and no treatment can guarantee results.",
  keyTakeaways: [
    "The SAINT® protocol is delivered over five consecutive days.",
    "Treatment duration is not the same as when you feel better — response varies individually.",
    "The 2020 open-label study reported an average 2.6 days to remission among responders.",
    "The 2022 RCT measured remission at four weeks, not on day five.",
    "No treatment guarantees results — individual outcomes vary.",
  ],

  sections: [
    {
      heading: "Treatment Duration vs Individual Response",
      body: (
        <>
          {P(
            "It is important to distinguish between how long a treatment takes to deliver and how quickly a person feels better. The SAINT® protocol is delivered over five consecutive days — ten short sessions per day with roughly 50-minute breaks. That is the treatment schedule.",
          )}
          {P(
            "When improvement happens is a separate question. Some people in the clinical trials showed measurable improvement during the five-day treatment week. Others improved in the days or weeks that followed. And some did not respond at all. The five-day schedule makes treatment faster to complete than six weeks of conventional TMS, but it does not guarantee that results come within five days.",
          )}
        </>
      ),
    },
    {
      heading: "What the Stanford Trials Showed",
      body: (
        <>
          {P(
            "In the 2020 open-label study of SAINT® (then called Stanford Neuromodulation Therapy), 19 of 21 completers (90.5%) met remission criteria after five days. Among those who responded, the average time to remission was 2.6 days. This study had no placebo comparison group, so the results are promising but not definitive.",
          )}
          {P(
            "In the 2022 double-blind, sham-controlled randomized trial, 78.6% of the active treatment group (11 of 14 analyzed participants) met remission criteria at four weeks, compared with 13.3% (2 of 15) of the sham group. Remission was measured at the four-week follow-up, not on day five. Both studies were small and conducted at a single site.",
          )}
          {P(
            <>
              These are trial statistics describing groups, not guaranteed
              outcomes for any individual. Learn more about{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                what SAINT® TMS is
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "When Improvement May Be Noticed",
      body: (
        <>
          {P(
            "Based on the trial data and clinical experience, some patients notice changes during the five-day treatment week — sometimes within the first two or three days. Others notice improvement in the days and weeks after treatment ends. The four-week follow-up in the randomized trial was designed to capture the point at which remission was assessed.",
          )}
          {P(
            "It is also important to know that not everyone responds. In the randomized trial, about one in five participants in the active group did not meet remission criteria at four weeks. Individual response depends on diagnosis, treatment history, and factors that are not fully understood.",
          )}
        </>
      ),
    },
    {
      heading: "How SAINT® Compares to Conventional TMS on Speed",
      body: (
        <>
          {P(
            "Conventional TMS is delivered one session per day, five days a week, for about four to six weeks — 30 to 36 sessions total. Improvement with conventional TMS is typically measured after several weeks of treatment.",
          )}
          {P(
            "SAINT® compresses the course into five consecutive days with 50 total sessions. The accelerated schedule means the treatment is completed in one week rather than six. For patients who cannot commit to six weeks of daily visits, or who are in a severe episode, this timeline may be a reason they seek out SAINT®.",
          )}
        </>
      ),
    },
    {
      heading: "What This Means for Patients",
      body: (
        <>
          {P(
            "If you are considering SAINT® TMS, the most important thing to understand is that the five-day schedule describes the treatment, not a promise. Some people respond quickly — within days. Others respond more gradually, and some do not respond at all.",
          )}
          {P(
            <>
              A consultation with a psychiatrist is the best way to set
              realistic expectations based on your diagnosis, medication
              history, and treatment goals.{" "}
              <a
                href="/contactus"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Request a consultation
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/how-fast-does-saint-tms-work#webpage`,
        name: "How Quickly Can SAINT® TMS Work?",
        url: `${SITE_URL}/learn/how-fast-does-saint-tms-work`,
        description:
          "How quickly SAINT® TMS works: the five-day protocol, trial data on time to improvement, and the difference between treatment duration and individual response.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/how-fast-does-saint-tms-work#faq`),
      breadcrumbJsonld(baseCrumbs("How Fast SAINT® TMS Works")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "What Happens During the 5 Days of SAINT®?",
      href: "/learn/what-to-expect-during-saint-tms",
      description: "A day-by-day guide to the treatment week",
    },
    {
      title: "SAINT® TMS vs Traditional TMS",
      href: "/learn/saint-vs-traditional-tms",
      description: "Schedule, targeting, and evidence compared",
    },
  ],
};
